import { Component, ViewChild, AfterViewInit, OnInit, Input } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { SharedDataService } from '../../../shared/shared-data-service';
export interface FeedRow {
  feedName: string;
  description: string;
  f1Score: number;
  cost: number;
  location: string;
  sensorsType: string;
}

@Component({
  selector: 'app-feeds-data-preview',
  templateUrl: './feeds-data-preview.component.html',
  styleUrls: ['./feeds-data-preview.component.scss'],
})
export class FeedsDataPreviewComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'feed_name',
    'description',
    'f1Score',
    'cost',
    'location',
    'supported_sensor_types',
  ];
  @Input() showBackToNewTask = false;

  dataSource = new MatTableDataSource<FeedRow>([]);

  @ViewChild(MatSort) sort!: MatSort;
  currentUser: any;
  industryTypes: any;
  tooltipImagePath: string;
  selectedIndustryList: any[] = [];
  feedsData: any[] = [];
  pointUser: boolean = false;
  currencySign: any;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private localStorageService: DataService,
    private sharedData: SharedDataService
  ) {
    this.localStorageService.darkTheme.subscribe((newValue) => {
      this.tooltipImagePath = newValue
        ? '../../../assets/images/union-dark.svg'
        : '../../../assets/images/union.svg';
    });
  }

  ngOnInit(): void {
    
    this.currencySign = environment.payCurrencySign;
    this.authService.user.pipe(take(1)).subscribe((user) => {
      this.currentUser = user;
      // Load industry types and feeds data
      this.loadFeedsData();
      this.loadIndustryTypes();
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'feed_name':
          return item.feedName;
        case 'description':
          return item.description;
        case 'f1Score':
          return item.f1Score;
        case 'cost':
          return item.cost;
        case 'location':
          return item.location;
        case 'supported_sensor_types':
          return item.sensorsType;
        default:
          return '';
      }
    };
  }

  private loadFeedsData(): void { 
    this.apiService
      .getAllInformationServices(this.currentUser?.id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
            this.feedsData = response?.informationServices;
        },
        error: (err) => {},
      });
  }

  // Load industry types from ApiService
  private loadIndustryTypes(): void {
    this.apiService.getIndustryTypes().pipe(take(1)).subscribe({
      next: (response) => {
        
        this.industryTypes = response.industriesList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          ...item,
          selected: false
        }));
        
      },
      error: (err) => {},
    });
  }

  industryTypeChange(industry: any, index: number) {
    // Handle selected industry type change logic here
    if (industry.selected) {
      this.selectedIndustryList.push(industry?.name);
      // Load data based on selected industries
      this.loadFilteredData();
    } else {
      // Remove industry from selected list
      const index = this.selectedIndustryList.findIndex(
        (selectedItem) => selectedItem === industry.name
      );
      this.selectedIndustryList.splice(index, 1);
      this.selectedIndustryList.length === 0 ? this.dataSource.data = [] : this.loadFilteredData();
    };
    
  };


  loadFilteredData(): void {
    const filteredData = this.feedsData.filter((feed: any) =>
      Array.isArray(feed.industries) &&
      feed.industries.some((industry: any) =>
        this.selectedIndustryList.includes(industry.name)
      )
    );

    this.dataSource.data = filteredData?.length ? this.mapToTableRows(filteredData) : [];
    this.applySort();
  }

  private mapToTableRows(data: any[]): FeedRow[] {
    return data
      .filter((item) => item.enabled)
      .map((item) => ({
        feedName: item.name,
        description: item.description,
        f1Score : item.f1Score,
        cost : item.cost,
        location: item.taskingArea,
        sensorsType: item.sensorTypes?.length
          ? item.sensorTypes.join(', ')
          : '—',
        
      }));  
  }

  private applySort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }


  backToNewTask(): void {
    this.sharedData.patchData({
      fromNewTask: false,
      restoreFromPreview: true
    });
    this.router.navigate(['/newtask'],{queryParams: { mode: 'task' }});
  }
  
  convertCostToPoints(cost: any){
    
    cost = parseFloat(cost);
    if (!this.currentUser.pointsEligible) {
      return `${this.currencySign} ${this.roundNumber(cost, 2)}`;
    }else{
      const conversionFactor = this.getConversionFactor();
      let points = Math.round(cost * conversionFactor);
      return `${points} Points`;
    }
  }

  private getConversionFactor(): number {
    const parts = this.currentUser.sensorConversionFactor.split(':');
    return parseFloat(parts[1]) / parseFloat(parts[0]);
  }

  roundNumber(value: number, decimals: number): string {
    if (Number.isInteger(value)) {
      return value.toString();
    }
    return value.toFixed(decimals).replace(/\.?0+$/, '');
  }
}
